import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { CalendarClock } from "lucide-react";
import { toast } from "sonner";

const TONE = { ok: "text-emerald-400", watch: "text-sky-300", renew_soon: "text-amber-300", urgent: "text-red-400", expired: "text-red-400" };

function CertRow({ c }) {
  return (
    <div data-testid={`cert-${c.key}`} className="rounded-md border border-[var(--border-subtle)] p-3 space-y-1">
      <div className="flex flex-wrap justify-between gap-2">
        <span className="text-slate-200">{c.name}</span>
        <span data-testid={`cert-${c.key}-days`} className={`font-mono ${TONE[c.status]}`}>{c.days_left} days left · expires {c.not_after.slice(0, 10)}</span>
      </div>
      <div className="text-slate-500">Source: {c.source}. {c.reminder_stage ? `${c.reminder_stage}-day reminder reached.` : `Next reminder at ${c.next_reminder_at_days} days.`}</div>
      <ol className="list-decimal pl-5 text-slate-400">{c.renewal_steps.map((s) => <li key={s}>{s}</li>)}</ol>
    </div>
  );
}

export function CertExpiryPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["cert-expiry"], queryFn: async () => (await api.get("/system/cert-expiry")).data });
  const check = useMutation({
    mutationFn: async () => (await api.post("/system/cert-expiry/check")).data,
    onSuccess: (r) => { qc.setQueryData(["cert-expiry"], r); toast.success(r.reminders_created.length ? `${r.reminders_created.length} reminder(s) added to Notifications` : "Checked; no new reminders due"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  return (
    <Panel title="Certificate expiry · renewal reminders" testId="cert-expiry-panel" actions={<CalendarClock className="w-4 h-4 text-slate-500" />}>
      <div className="p-4 space-y-2 text-xs">
        <p className="text-slate-400">Checked daily. Reminders go to Notifications at {data?.thresholds_days?.join(", ") || "90, 60, 30, 14, 7, 3, 1"} days before expiry. Read-only: nothing is renewed automatically.</p>
        {data?.certificates?.map((c) => <CertRow key={c.key} c={c} />)}
        <Button size="sm" variant="outline" data-testid="cert-expiry-check-btn" onClick={() => check.mutate()} disabled={check.isPending} className="border-[var(--border-subtle)]">Check now</Button>
      </div>
    </Panel>
  );
}
